/**
 * Donnes d'un match de championnat par équipes.
 *
 * Un match d'équipes se joue en duplicate : la même donne est jouée aux deux
 * tables, l'attaque changeant de camp. Les deux tables sont donc affichées côte
 * à côte et synchronisées.
 *
 * L'accès est restreint côté backend (membres des deux équipes et admins) :
 * les appels doivent porter le jeton de session.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import ReplayDualMode, { type ReplayPaneSpec } from '../ReplayDualMode';
import { useApp } from '../../contexts/AppContext';
import type { Session } from '../../types/app';
import type { GameHistory } from '../../game/gameHistory';
import {
  buildGameHistoryFromReplayTable,
  buildReplaySeatLabels,
  gameHistoryToTrainingGameData,
  type ReplayTable,
  type ReplayDonneListItem,
  type CompareReplayDealEntry,
  type CompareReplayTeamSide,
} from '../../utils/competitionReplay';
import { BACKEND_URL } from '../../config';

interface TeamAccessMember {
  equipe_id: number;
  pseudo: string;
  pseudo_display?: string;
  role?: string;
}

interface TeamAccessInfo {
  equipe1_id: number | null;
  equipe2_id: number | null;
  equipe1_nom: string | null;
  equipe2_nom: string | null;
  viewer_equipe_ids: number[];
  is_admin_view?: boolean;
  members: TeamAccessMember[];
}

interface TeamMatchReplayViewProps {
  matchId: number;
  equipe1Nom: string;
  equipe2Nom: string;
  equipe1Id?: number;
  equipe2Id?: number;
  /** Date du match (affichée dans l'historique entraînement). */
  matchDate?: string | null;
  phaseId?: number | null;
  divisionId?: number | null;
  session: Session;
  onBack: () => void;
}

interface PreparedTable {
  table: ReplayTable;
  history: GameHistory;
  numtable: number;
  label: string;
  scoreText: string;
  attackerDisplay: string;
  equipeId: number | null;
}

function fmtScore(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  return n > 0 ? `+${n}` : `${n}`;
}

function normalize(p: string): string {
  return p.toLowerCase().replace(/¶/g, ' ').trim();
}

function apiErrorMessage(json: { error?: string; details?: string | null }): string {
  if (json.error === 'not_team_member') {
    return "Ces donnes sont réservées aux membres des deux équipes du match.";
  }
  if (json.error === 'missing_token') {
    return 'Reconnectez-vous pour consulter ces donnes.';
  }
  if (typeof json.details === 'string' && json.details.length > 0) return json.details;
  return json.error || 'Erreur de chargement';
}

export default function TeamMatchReplayView({
  matchId,
  equipe1Nom,
  equipe2Nom,
  equipe1Id,
  equipe2Id,
  matchDate,
  phaseId,
  divisionId,
  session,
  onBack,
}: TeamMatchReplayViewProps) {
  const { startTraining } = useApp();

  const [donnes, setDonnes] = useState<ReplayDonneListItem[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [selectedDonne, setSelectedDonne] = useState<number | null>(null);
  const [prepared, setPrepared] = useState<PreparedTable[] | null>(null);
  const [replayLoading, setReplayLoading] = useState(false);
  const [replayError, setReplayError] = useState<string | null>(null);
  const [exportLoading, setExportLoading] = useState(false);

  const authHeaders = useMemo(
    () => (session.token ? { Authorization: `Bearer ${session.token}` } : undefined),
    [session.token],
  );

  // Liste des donnes du match
  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/competition/replay/${matchId}`, {
          headers: authHeaders,
        });
        const json = await res.json();
        if (!json.success) throw new Error(apiErrorMessage(json));
        if (cancelled) return;
        setDonnes(json.data?.donnes ?? []);
        setListError(null);
      } catch (err) {
        if (!cancelled) setListError(err instanceof Error ? err.message : 'Erreur de chargement');
      } finally {
        if (!cancelled) setListLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [matchId, authHeaders]);

  /**
   * Prépare les deux tables d'une donne : la table où joue l'équipe du
   * spectateur passe à gauche (table 1 par défaut pour un admin extérieur).
   */
  const loadDonne = useCallback(async (numdonne: number) => {
    setReplayLoading(true);
    setReplayError(null);
    setSelectedDonne(numdonne);
    try {
      const res = await fetch(`${BACKEND_URL}/api/competition/replay/${matchId}/${numdonne}`, {
        headers: authHeaders,
      });
      const json = await res.json();
      if (!json.success) throw new Error(apiErrorMessage(json));

      const tables = ([...(json.data?.tables ?? [])] as ReplayTable[]).sort(
        (a, b) => Number(a.numtable) - Number(b.numtable),
      );
      if (tables.length === 0) throw new Error(`Donne ${numdonne} : aucune table enregistrée`);

      const access = (json.data?.team_access ?? null) as TeamAccessInfo | null;
      const equipeByPseudo = new Map<string, number>();
      for (const m of access?.members ?? []) {
        equipeByPseudo.set(normalize(m.pseudo), Number(m.equipe_id));
      }
      const teamNameById = new Map<number, string>();
      if (access?.equipe1_id != null) {
        teamNameById.set(Number(access.equipe1_id), access.equipe1_nom || equipe1Nom);
      }
      if (access?.equipe2_id != null) {
        teamNameById.set(Number(access.equipe2_id), access.equipe2_nom || equipe2Nom);
      }

      const items: PreparedTable[] = tables.map((table) => {
        const numtable = Number(table.numtable);
        const attacker =
          table.joueurs.find((j) => j.is_preneur)
          ?? table.joueurs.find((j) => j.position === Number(table.preneur));
        const attackerPseudo = attacker?.pseudo ?? '';
        const attackerDisplay = (attacker?.pseudo_display || attackerPseudo || '?').replace(/¶/g, ' ');
        const equipeId = equipeByPseudo.get(normalize(attackerPseudo)) ?? null;
        const teamNom = equipeId != null ? teamNameById.get(equipeId) : undefined;

        return {
          table,
          history: buildGameHistoryFromReplayTable(table, session.pseudo),
          numtable,
          label: teamNom
            ? `${teamNom} attaque (T${numtable})`
            : `Table ${numtable} — ${attackerDisplay} attaque`,
          scoreText: fmtScore(table.scoreduplicate),
          attackerDisplay,
          equipeId,
        };
      });

      const viewerTeams = new Set((access?.viewer_equipe_ids ?? []).map(Number));
      const myIndex = items.findIndex((it) => it.equipeId != null && viewerTeams.has(it.equipeId));
      const ordered = myIndex > 0 ? [items[myIndex], ...items.filter((_, i) => i !== myIndex)] : items;

      setPrepared(ordered);
    } catch (err) {
      setReplayError(err instanceof Error ? err.message : 'Erreur de chargement');
      setPrepared(null);
    } finally {
      setReplayLoading(false);
    }
  }, [matchId, authHeaders, session.pseudo, equipe1Nom, equipe2Nom]);

  const currentIndex = useMemo(
    () => donnes.findIndex((d) => Number(d.numdonne) === selectedDonne),
    [donnes, selectedDonne],
  );

  const goToDonne = useCallback((offset: number) => {
    const next = donnes[currentIndex + offset];
    if (next) void loadDonne(Number(next.numdonne));
  }, [donnes, currentIndex, loadDonne]);

  /** Export de toutes les donnes du match vers l'entraînement (deux tables en variantes). */
  const openInTraining = useCallback(async () => {
    if (donnes.length === 0) return;
    setExportLoading(true);
    setReplayError(null);
    try {
      let resolvedEquipe1Id = equipe1Id != null ? Number(equipe1Id) : undefined;
      let resolvedEquipe2Id = equipe2Id != null ? Number(equipe2Id) : undefined;

      const deals: CompareReplayDealEntry[] = await Promise.all(
        donnes.map(async (d) => {
          const res = await fetch(
            `${BACKEND_URL}/api/competition/replay/${matchId}/${d.numdonne}`,
            { headers: authHeaders },
          );
          const json = await res.json();
          if (!json.success) throw new Error(apiErrorMessage(json));

          const tables = ([...(json.data?.tables ?? [])] as ReplayTable[]).sort(
            (a, b) => Number(a.numtable) - Number(b.numtable),
          );
          if (tables.length === 0) throw new Error(`Donne ${d.numdonne} : aucune table`);

          const access = (json.data?.team_access ?? null) as TeamAccessInfo | null;
          if (access?.equipe1_id != null) resolvedEquipe1Id = Number(access.equipe1_id);
          if (access?.equipe2_id != null) resolvedEquipe2Id = Number(access.equipe2_id);
          const equipeByPseudo = new Map<string, number>();
          for (const m of access?.members ?? []) {
            equipeByPseudo.set(normalize(m.pseudo), Number(m.equipe_id));
            if (m.pseudo_display) {
              equipeByPseudo.set(normalize(m.pseudo_display), Number(m.equipe_id));
            }
          }
          const teamById = new Map<number, CompareReplayTeamSide>();
          const e1: CompareReplayTeamSide = {
            id: access?.equipe1_id != null
              ? Number(access.equipe1_id)
              : (equipe1Id != null ? Number(equipe1Id) : 1),
            nom: (access?.equipe1_nom || equipe1Nom).trim() || equipe1Nom,
          };
          const e2: CompareReplayTeamSide = {
            id: access?.equipe2_id != null
              ? Number(access.equipe2_id)
              : (equipe2Id != null ? Number(equipe2Id) : 2),
            nom: (access?.equipe2_nom || equipe2Nom).trim() || equipe2Nom,
          };
          teamById.set(e1.id, e1);
          teamById.set(e2.id, e2);

          const resolveAttackTeam = (
            attackerPseudo: string,
            attackerDisplay: string,
            tableIdx: number,
          ): CompareReplayTeamSide => {
            const fromAttacker =
              equipeByPseudo.get(normalize(attackerPseudo))
              ?? equipeByPseudo.get(normalize(attackerDisplay));
            if (fromAttacker != null && teamById.has(fromAttacker)) {
              return teamById.get(fromAttacker)!;
            }
            // Dernier recours : colonne / numtable (T1 → équipe 1, T2 → équipe 2)
            return tableIdx <= 1 ? e1 : e2;
          };

          const variants = tables.map((table) => {
            const tIdx = Number(table.numtable);
            const attacker =
              table.joueurs.find((j) => j.is_preneur)
              ?? table.joueurs.find((j) => j.position === Number(table.preneur));
            const attackerPseudo = attacker?.pseudo ?? '';
            const playerLabel = (attacker?.pseudo_display || attackerPseudo || `Table ${tIdx}`)
              .replace(/¶/g, ' ');
            const scoreText = fmtScore(table.scoreduplicate);
            const history = buildGameHistoryFromReplayTable(table, session.pseudo);
            const resolvedAttack = resolveAttackTeam(attackerPseudo, playerLabel, tIdx);
            const resolvedDefense = resolvedAttack.id === e1.id ? e2 : e1;

            return {
              tableIdx: tIdx,
              label: `${resolvedAttack.nom} (${scoreText})`,
              scoreText,
              playerLabel,
              teamLabel: resolvedAttack.nom,
              attackTeam: resolvedAttack,
              defenseTeam: resolvedDefense,
              // Mains propres à chaque table (ajustement chien/écart du preneur inclus).
              // Ne pas écraser avec les mains de la table 0 : les preneurs diffèrent.
              gameData: gameHistoryToTrainingGameData(history),
              replaySeatLabels: buildReplaySeatLabels(table),
            };
          });

          return {
            dealIndex: Number(d.numdonne),
            label: `Donne ${d.numdonne}`,
            variants,
          };
        }),
      );

      deals.sort((a, b) => a.dealIndex - b.dealIndex);
      const selectedIndex = Math.max(
        0,
        deals.findIndex((deal) => deal.dealIndex === (selectedDonne ?? deals[0]?.dealIndex)),
      );

      sessionStorage.setItem(
        'library_load_request',
        JSON.stringify({
          compareReplayBatch: {
            title: `${equipe1Nom} vs ${equipe2Nom}`,
            deals,
            selectedIndex,
            selectedVariantIndex: 0,
          },
          mode: 'replay',
          position: 0,
          replayAllBots: true,
          historyContext: {
            kind: 'team_champ',
            teamChamp: {
              matchId,
              equipe1Nom,
              equipe2Nom,
              equipe1Id: resolvedEquipe1Id,
              equipe2Id: resolvedEquipe2Id,
              matchDate: matchDate || undefined,
              phaseId: phaseId ?? undefined,
              divisionId: divisionId ?? undefined,
              dealNum: deals[selectedIndex]?.dealIndex,
            },
          },
        }),
      );

      startTraining({
        sessionId: '',
        displayMode: 'cards_on_table',
        showTimeline: true,
        botConfig: { disabledBots: new Set() },
      });
    } catch (err) {
      setReplayError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setExportLoading(false);
    }
  }, [
    donnes, matchId, authHeaders, selectedDonne, session.pseudo,
    equipe1Nom, equipe2Nom, equipe1Id, equipe2Id, matchDate,
    phaseId, divisionId, startTraining,
  ]);

  // ==========================================
  // Rendu
  // ==========================================

  if (selectedDonne !== null) {
    const left = prepared?.[0];
    const right = prepared?.[1];

    const leftSpec: ReplayPaneSpec | null = left
      ? { history: left.history, label: left.label, sublabel: left.scoreText }
      : null;
    const rightSpec: ReplayPaneSpec | null = right
      ? { history: right.history, label: right.label, sublabel: right.scoreText }
      : null;

    return (
      <div className="team-replay-wrapper">
        <div className="team-replay-toolbar">
          <button type="button" className="team-replay-back" onClick={() => { setSelectedDonne(null); setPrepared(null); }}>
            ← Donnes du match
          </button>
          <div className="team-replay-donne-nav">
            <button
              type="button"
              className="team-replay-nav-btn"
              onClick={() => goToDonne(-1)}
              disabled={currentIndex <= 0 || replayLoading}
              title="Donne précédente"
            >
              ◀
            </button>
            <span className="team-replay-donne-label">Donne {selectedDonne}</span>
            <button
              type="button"
              className="team-replay-nav-btn"
              onClick={() => goToDonne(1)}
              disabled={currentIndex < 0 || currentIndex >= donnes.length - 1 || replayLoading}
              title="Donne suivante"
            >
              ▶
            </button>
          </div>
        </div>

        <div className="team-replay-stage">
          {replayLoading && <div className="compet-loading">Chargement de la donne…</div>}
          {!replayLoading && replayError && <div className="compet-error">{replayError}</div>}
          {!replayLoading && !replayError && leftSpec && (
            <ReplayDualMode
              left={leftSpec}
              right={rightSpec}
              rightPlaceholder="Seule une table est enregistrée pour cette donne."
              actions={
                <button
                  type="button"
                  className="replay-btn"
                  onClick={() => void openInTraining()}
                  disabled={exportLoading}
                  title="Rejouer les donnes du match en entraînement"
                >
                  {exportLoading ? 'Chargement…' : '🎯 Entraînement'}
                </button>
              }
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="team-replay-list">
      <div className="team-replay-toolbar">
        <button type="button" className="team-replay-back" onClick={onBack}>
          ← Résultats
        </button>
        <span className="team-replay-title">{equipe1Nom} vs {equipe2Nom}</span>
      </div>

      {listLoading && <div className="compet-loading">Chargement des donnes…</div>}
      {!listLoading && listError && <div className="compet-error">{listError}</div>}
      {!listLoading && !listError && donnes.length === 0 && (
        <div className="compet-empty">Aucune donne enregistrée pour ce match</div>
      )}

      {!listLoading && donnes.length > 0 && (
        <>
          <button
            type="button"
            className="cia-replay-all-btn"
            disabled={exportLoading}
            onClick={() => void openInTraining()}
          >
            {exportLoading
              ? 'Chargement…'
              : `Ouvrir les ${donnes.length} donnes en entraînement`}
          </button>
          <ul className="cia-replay-donne-list">
            {donnes.map((d) => (
              <li key={d.numdonne}>
                <button
                  type="button"
                  className="cia-replay-donne-btn"
                  onClick={() => void loadDonne(Number(d.numdonne))}
                >
                  <span className="cia-replay-donne-num">Donne {d.numdonne}</span>
                  <span className="cia-replay-donne-scores">
                    {fmtScore(d.score_table1)} / {fmtScore(d.score_table2)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      {replayError && <div className="compet-error">{replayError}</div>}
    </div>
  );
}
